import { formatMinutesToHoursString } from "@components/inputs/date/utils";
import SimpleTable, { ISimpleTableRow } from "@components/simpleTable/SimpleTable";
import { IColumn } from "@components/table";
import { IPrPayslipEmploymentEntity } from "@odata/GeneratedEntityTypes";
import { PrEmploymentTypeCode, PrExtraSalaryTypeCode } from "@odata/GeneratedEnums";
import { getCurrentEmploymentBc } from "@pages/payroll/payslip/Payslip.utils";
import i18next from "i18next";
import React from "react";

import { DASH_CHARACTER } from "../../../constants";
import { TextAlign } from "../../../enums";
import NumberType from "../../../types/Number";
import { FormStorage } from "../../../views/formView/FormStorage";

interface IProps {
    storage: FormStorage;
}

function getColumns(): IColumn[] {
    return [{
        id: "type",
        label: i18next.t("Payslip:ExtraSalariesOverviewTable.Type"),
        textAlign: TextAlign.Left,
    }, {
        id: "thisMonth",
        label: i18next.t("Payslip:ExtraSalariesOverviewTable.ThisMonth"),
        textAlign: TextAlign.Left,
    }, {
        id: "lastMonth",
        label: i18next.t("Payslip:ExtraSalariesOverviewTable.LastMonth"),
        textAlign: TextAlign.Left,
    }, {
        id: "freeThisMonth",
        label: i18next.t("Payslip:ExtraSalariesOverviewTable.FreeThisMonth"),
        textAlign: TextAlign.Left,
    }, {
        id: "carryOver",
        label: i18next.t("Payslip:ExtraSalariesOverviewTable.ToCarryOver"),
        textAlign: TextAlign.Left,
    }];
}

const overViewTableFormatter = (value: number): string => {
    if (!NumberType.isValid(value)) {
        return DASH_CHARACTER;
    }
    return formatMinutesToHoursString(value);
};

export const ExtraSalariesOverviewTable: React.FC<IProps> = ({
                                                                 storage
                                                             }) => {
    const employmentBc = getCurrentEmploymentBc(storage);
    const employment: IPrPayslipEmploymentEntity = storage.getValue(employmentBc);
    const holidaySalaryItem = employment.ExtraSalaries.find(es => es.TypeCode === PrExtraSalaryTypeCode.Holiday);
    const overtimeSalaryItem = employment.ExtraSalaries.find(es => es.TypeCode === PrExtraSalaryTypeCode.Overtime);

    const overtimeThisMonth = employment.OvertimeThisMonth ?? 0;
    const holidayWorkedThisMonth = employment.HolidayWorked ?? 0;
    const carryOverOvertime = Math.max(0, overtimeThisMonth + employment.OvertimePrevious - employment.OvertimeCompensatory - (overtimeSalaryItem?.LengthOverride ?? 0));
    const carryOverHoliday = Math.max(0, holidayWorkedThisMonth + employment.HolidayPrevious - employment.HolidayCompensatory - (holidaySalaryItem?.LengthOverride ?? 0));

    let rows: ISimpleTableRow[] = [{
        id: "overtime",
        values: {
            type: storage.t("Payslip:ExtraSalariesOverviewTable.Overtime").toString(),
            thisMonth: overViewTableFormatter(overtimeThisMonth),
            lastMonth: overViewTableFormatter(employment.OvertimePrevious),
            freeThisMonth: overViewTableFormatter(employment.OvertimeCompensatory),
            carryOver: overViewTableFormatter(carryOverOvertime),
        }
    }, {
        id: "holiday",
        values: {
            type: storage.t("Payslip:ExtraSalariesOverviewTable.Holiday").toString(),
            thisMonth: overViewTableFormatter(holidayWorkedThisMonth),
            lastMonth: overViewTableFormatter(employment.HolidayPrevious),
            freeThisMonth: overViewTableFormatter(employment.HolidayCompensatory),
            carryOver: overViewTableFormatter(carryOverHoliday),
        }
    }];

    if ([PrEmploymentTypeCode.WorkAgreementDPP, PrEmploymentTypeCode.WorkAgreementDPC].includes(employment.Employment.TypeCode)) {
        // https://solitea-cz.atlassian.net/browse/DEV-42355
        rows = rows.filter(r => r.id !== "overtime");
    }

    return <SimpleTable rows={rows} columns={getColumns()}/>;
};